import type { AnyRouter } from '@tanstack/react-router'
import { validateUiAction, type NavTarget, type UiActionContext, type UiActionRefusal } from '@/lib/aiNavTargets'

// ── Bhookly AI ui_action executor ───────────────────────────────────────────
// URL-first: every target the registry allows is reached by navigating to its
// route with its fixed search params. Tabs and sections are driven by their
// route's validateSearch, so a navigation alone opens them.
//
// Clicking an element after the navigation (dialogs, drawers, data-ai-target
// buttons) is a separate, later step. validateUiAction refuses any action that
// carries a click target until that step exists, so this executor only ever
// receives navigations.

export type UiActionOutcome = 'navigated' | 'refused'

export type UiActionResult =
  | { outcome: 'navigated'; target: NavTarget }
  | { outcome: 'refused'; reason: UiActionRefusal | 'navigation_failed'; target?: NavTarget }

type NavigatingRouter = Pick<AnyRouter, 'navigate'>

/** Full paths of every route the router registers (`router.flatRoutes`). */
export function knownRoutesFromRouter(router: Pick<AnyRouter, 'flatRoutes'>): Set<string> {
  return new Set(router.flatRoutes.map((r) => r.fullPath as string))
}

/**
 * Opens a validated target: navigates to the registry's own route and search
 * (never the action's copies, even though validation proved them equal).
 */
export async function executeNavTarget(router: NavigatingRouter, target: NavTarget): Promise<UiActionResult> {
  try {
    await router.navigate({ to: target.route, search: { ...target.search } })
    return { outcome: 'navigated', target }
  } catch {
    return { outcome: 'refused', reason: 'navigation_failed', target }
  }
}

/** Validates a copilot `ui_action` and, if the registry allows it, executes it. */
export async function runUiAction(
  router: NavigatingRouter,
  action: unknown,
  ctx: UiActionContext,
): Promise<UiActionResult> {
  const v = validateUiAction(action, ctx)
  if (!v.ok) return { outcome: 'refused', reason: v.reason }
  return executeNavTarget(router, v.target)
}
